import dayjs from "dayjs";
import {
  normalizeOrderSearchTerm,
  orderRowMatchesOrderSearchTerm,
} from "../../../utils/orders/normalizeOrderSearchTerm";
import { shouldShowOrderOnGlobalBoard } from "../visibility/orderVisibilitySsot";
import { isHotelCxlDropRisk } from "../../../utils/orders/resolveHotelCxlDropRisk";

/**
 * User-applied filters for Gimmonix / classic hotel orders (client-side only).
 * Same shape as HotelsFilters — single source for defaults + applyUserFiltersToHotelOrders.
 */
export const HOTEL_DASHBOARD_DEFAULT_FILTERS = {
  orderStatus: "active",
  order_dates: { start: "", end: "" },
  staying_dates: { start: "", end: "" },
  agent_email: "",
  hotel_name: "",
  main_contact: "",
  order_id: "",
  refundableuntil: "",
  freeCancellationDays: null,
  cxlDropRisk: false,
  notPaid: false,
  showCancelled: false,
};

/** Agent hotel tab: recent pending + paid rows. Paid stays after checkout are hidden by default. */
export const AGENT_HOTEL_ORDERS_PAGE_SIZE = 20;

export const AGENT_HOTEL_DASHBOARD_DEFAULT_FILTERS = {
  ...HOTEL_DASHBOARD_DEFAULT_FILTERS,
  orderStatus: "pending_and_paid",
};

export function resolveHotelDashboardDefaultFilters({ forAgent = false, forAdmin = false } = {}) {
  if (forAgent) return AGENT_HOTEL_DASHBOARD_DEFAULT_FILTERS;
  if (forAdmin) {
    return { ...HOTEL_DASHBOARD_DEFAULT_FILTERS, orderStatus: "all" };
  }
  return HOTEL_DASHBOARD_DEFAULT_FILTERS;
}

function parseDDMMYYYY(dateStr) {
  return dayjs(dateStr, "DD/MM/YYYY").toDate();
}

function isHotelPaidFlag(item) {
  return item?.paid === true || item?.paid === "true" || item?.paid === 1;
}

/**
 * Skip the default "paid + checkout already passed" hide only for explicit history views.
 * Agent `pending_and_paid` and admin `all` defaults must NOT skip.
 */
export function shouldSkipHotelPaidPastCheckoutHide(filters = {}) {
  const orderStatus = filters?.orderStatus || "active";
  const hasDateFilters = Boolean(
    (filters?.order_dates?.start && filters?.order_dates?.end) ||
      (filters?.staying_dates?.start && filters?.staying_dates?.end)
  );
  return (
    hasDateFilters ||
    orderStatus === "paid" ||
    orderStatus === "cancelled"
  );
}

export function applyUserFiltersToHotelOrders(orders, filters) {
  if (!orders || !Array.isArray(orders)) {
    return [];
  }
  if (!filters || typeof filters !== "object") {
    return orders;
  }

  return orders.filter((item) => {
    const {
      orderStatus = "active",
      order_dates = { start: "", end: "" },
      staying_dates = { start: "", end: "" },
      hotel_name,
      main_contact,
      order_id,
      refundableuntil,
      freeCancellationDays,
      cxlDropRisk,
      agent_email,
      notPaid,
      showCancelled,
    } = filters;

    if (cxlDropRisk && !isHotelCxlDropRisk(item)) {
      return false;
    }

    const paid = isHotelPaidFlag(item);

    if (orderStatus === "cancelled") {
      if (!item.cancelled) return false;
    } else if (orderStatus === "all") {
      if (item.cancelled && !showCancelled) return false;
    } else if (orderStatus === "pending_and_paid") {
      if (item.cancelled) return false;
    } else if (orderStatus === "active") {
      if (item.cancelled && !showCancelled) return false;
    } else {
      if (item.cancelled) return false;
      if (orderStatus === "paid" && !paid) return false;
      if (orderStatus === "pending" && paid) return false;
    }

    /*
     * ברירת מחדל: להסתיר שהייה שכבר נסגרה רק כשההזמנה שולמה.
     * ממתינות לתשלום נשארות גם אחרי checkout. היסטוריה — סטטוס שולם / טווח תאריכים.
     */
    const skipDefaultCheckout = shouldSkipHotelPaidPastCheckoutHide({
      orderStatus,
      order_dates,
      staying_dates,
    });

    if (!skipDefaultCheckout && paid && item.checkout) {
      try {
        const checkoutDate = parseDDMMYYYY(item.checkout);
        if (!Number.isNaN(checkoutDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (checkoutDate < today) return false;
        }
      } catch {
        /* keep */
      }
    }

    if (shouldShowOrderOnGlobalBoard(item)) {
      return true;
    }

    const orderDateMatch = order_dates?.start
      ? parseDDMMYYYY(item.creationdate) >= parseDDMMYYYY(order_dates.start) &&
        parseDDMMYYYY(item.creationdate) <= parseDDMMYYYY(order_dates.end)
      : true;

    const stayingDateMatch = staying_dates?.start
      ? parseDDMMYYYY(item.checkin) >= parseDDMMYYYY(staying_dates.start) &&
        parseDDMMYYYY(item.checkout) <= parseDDMMYYYY(staying_dates.end)
      : true;

    const hotelNameMatch = hotel_name
      ? (item.hotelname || "").toLowerCase().includes(hotel_name.toLowerCase())
      : true;

    const mainContactMatch = main_contact
      ? (item.fullname || "").toLowerCase().includes(main_contact.toLowerCase())
      : true;

    const orderIdMatch = order_id
      ? orderRowMatchesOrderSearchTerm(item, normalizeOrderSearchTerm(order_id))
      : true;

    const cancelUntilMatch = refundableuntil
      ? dayjs(item.refundableuntil, "DD/MM/YYYY").toDate() <= refundableuntil
      : true;

    let freeCancellationMatch = true;
    if (
      freeCancellationDays != null &&
      (freeCancellationDays === 7 ||
        freeCancellationDays === 14 ||
        freeCancellationDays === 21 ||
        freeCancellationDays === 28)
    ) {
      const isPaid = isHotelPaidFlag(item);
      const rawRefund = item.refundableuntil;
      const hasValidRefund =
        rawRefund &&
        String(rawRefund).trim() !== "" &&
        String(rawRefund).toLowerCase() !== "none";
      if (!hasValidRefund || isPaid) {
        freeCancellationMatch = false;
      } else {
        let orderRefundableUntil = dayjs(rawRefund, "DD/MM/YYYY");
        if (!orderRefundableUntil.isValid()) {
          orderRefundableUntil = dayjs(rawRefund);
        }
        if (!orderRefundableUntil.isValid()) {
          freeCancellationMatch = false;
        } else {
          const todayStart = dayjs().startOf("day");
          const limitDate = dayjs()
            .add(freeCancellationDays, "day")
            .endOf("day");
          const refundStart = orderRefundableUntil.startOf("day");
          freeCancellationMatch =
            !refundStart.isBefore(todayStart) && !refundStart.isAfter(limitDate);
        }
      }
    }

    const agentEmailMatch = agent_email
      ? (item.email || "")
          .toLowerCase()
          .includes(agent_email.toLowerCase())
      : true;

    const notPaidMatch = notPaid ? !item.paid : true;
    const cxlDropRiskMatch = cxlDropRisk ? isHotelCxlDropRisk(item) : true;

    return (
      orderDateMatch &&
      stayingDateMatch &&
      hotelNameMatch &&
      mainContactMatch &&
      orderIdMatch &&
      cancelUntilMatch &&
      freeCancellationMatch &&
      agentEmailMatch &&
      notPaidMatch &&
      cxlDropRiskMatch
    );
  });
}
